import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import StatusPill from '../components/StatusPill';
import { builderAvailability, isPrimarySpineSession, nextProjectAction, projectActionBlockMessage, spineNodeForSession, stageFor } from '../components/projectSpineLifecycle';
import { useAuth } from '../context/AuthContext';
import ProjectSpine from './ProjectSpine';
import './Work.css';

export const PROJECT_SECTIONS = [
  { key: 'define', label: 'Definir', description: 'Marca, intención, personas y decisiones.' },
  { key: 'design', label: 'Diseñar', description: 'Mapa, stack, arquitectura y revisiones.' },
  { key: 'build', label: 'Construir', description: 'Paquetes listos y ejecución del Builder.' },
  { key: 'deliver', label: 'Entregar', description: 'Pruebas, runbooks y evidencia CI/CD.' },
];

export function contextQuery({ userId, username, repoId, section }) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (username) params.set('username', username);
  if (repoId) params.set('repo_id', String(repoId));
  if (section) params.set('section', section);
  return params.toString();
}

export function defaultSection(spine) {
  return spine?.business_intents?.length ? 'build' : 'define';
}

export function sectionForProjectChange(currentRepoId, nextRepoId, activeSection) {
  return String(currentRepoId) === String(nextRepoId) ? activeSection : undefined;
}

export function projectStatus(spine) {
  const next = nextProjectAction(spine);
  if (!next) return { label: 'Sin acciones pendientes', color: 'green', owner: 'FORGE', detail: 'El proyecto no tiene acciones pendientes.' };
  if (next.key === 'define_intent') return { label: 'Por definir', color: 'amber', owner: next.owner, detail: next.detail };
  if (['resolve_findings', 'approve_context', 'approve_forge_master', 'approve_map'].includes(next.key)) return { label: 'Requiere decisiones', color: 'amber', owner: next.owner, detail: next.detail };
  if (['builder_ready', 'builder_blocked', 'in_execution'].includes(next.key)) return { label: 'En construcción', color: 'cyan', owner: next.owner, detail: next.detail };
  if (next.key === 'review_delivery') return { label: 'Con entregas', color: 'green', owner: next.owner, detail: next.detail };
  return { label: 'En diseño', color: 'blue', owner: next.owner, detail: next.detail };
}

function Work() {
  const { apiUrl, userId, username } = useOutletContext();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const repoId = params.get('repo_id');
  const requestedSection = params.get('section');
  const [repos, setRepos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [spine, setSpine] = useState(null);
  const [loadedRepoId, setLoadedRepoId] = useState(null);
  const [error, setError] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);

  const loadProject = useCallback(async () => {
    if (!userId) return;
    try {
      const reposResponse = await fetch(`${apiUrl}/api/repos/?user_id=${userId}`, { credentials: 'include' });
      const availableRepos = await reposResponse.json();
      if (!reposResponse.ok) throw new Error('No se pudieron cargar los proyectos.');
      setRepos(Array.isArray(availableRepos) ? availableRepos : []);
      if (!repoId) {
        setSessions([]);
        setSpine(null);
        setLoadedRepoId(null);
        return;
      }
      setLoadedRepoId(null);
      setSessions([]);
      setSpine(null);
      const [sessionsResponse, spineResponse] = await Promise.all([
        fetch(`${apiUrl}/api/specs/?user_id=${userId}&repository_id=${repoId}`, { credentials: 'include' }),
        fetch(`${apiUrl}/api/project-spine/${repoId}?user_id=${userId}`, { credentials: 'include' }),
      ]);
      const [sessionData, spineData] = await Promise.all([sessionsResponse.json(), spineResponse.json()]);
      setSessions(sessionsResponse.ok && Array.isArray(sessionData) ? sessionData : []);
      setSpine(spineResponse.ok ? spineData : null);
      setLoadedRepoId(spineResponse.ok ? String(repoId) : null);
      setError(spineResponse.ok ? null : (spineData.detail || 'No se pudo cargar el proyecto.'));
    } catch (loadError) {
      setError(loadError.message || 'No se pudo cargar el espacio de proyecto.');
    }
  }, [apiUrl, repoId, userId]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  const selectedRepo = useMemo(() => repos.find((repo) => String(repo.id) === String(repoId)), [repos, repoId]);
  const activeSection = PROJECT_SECTIONS.some((item) => item.key === requestedSection) ? requestedSection : defaultSection(spine);
  const status = projectStatus(spine);
  const projectAction = nextProjectAction(spine);
  const projectActionBlock = projectActionBlockMessage(projectAction, user?.roles || [], Boolean(user?.can_admin));
  const progress = useMemo(() => {
    const nodes = spine?.business_intents?.[0]?.nodes || [];
    return { total: nodes.length, completed: nodes.filter((node) => node.completed).length };
  }, [spine]);
  const nodesBySession = useMemo(() => new Map((spine?.business_intents || []).flatMap((intent) => intent.nodes || []).map((node) => [node.spec_session_id, node])), [spine]);
  const workItems = useMemo(() => sessions.map((session) => {
    const node = nodesBySession.get(session.id) || spineNodeForSession(spine, session.id);
    return { session, node, stage: stageFor({ session, node }), availability: builderAvailability(spine, node) };
  }).filter((item) => !item.node || isPrimarySpineSession(item.node, item.session.id)), [sessions, spine, nodesBySession]);

  useEffect(() => {
    if (!repoId || requestedSection || !spine || loadedRepoId !== String(repoId)) return;
    navigate(`/work?${contextQuery({ userId, username, repoId, section: defaultSection(spine) })}`, { replace: true });
  }, [loadedRepoId, navigate, repoId, requestedSection, spine, userId, username]);

  const changeProject = (event) => {
    const nextRepoId = event.target.value;
    if (!nextRepoId) return navigate(`/work?${contextQuery({ userId, username })}`);
    navigate(`/work?${contextQuery({
      userId,
      username,
      repoId: nextRepoId,
      section: sectionForProjectChange(repoId, nextRepoId, activeSection),
    })}`);
  };

  const prepareLocalDemo = async (reset = false) => {
    setDemoBusy(true);
    try {
      const response = await fetch(`${apiUrl}/api/project-spine/demo/local/${reset ? 'reset' : 'prepare'}?user_id=${userId}`, {
        method: 'POST', credentials: 'include',
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'No se pudo preparar la demo local.');
      navigate(`/work?${contextQuery({ userId, username, repoId: payload.repository_id, section: 'define' })}`);
    } catch (demoError) {
      setError(demoError.message || 'No se pudo preparar la demo local.');
    } finally {
      setDemoBusy(false);
    }
  };

  if (!repoId) return <div className="forge-page workspace-selector-page">
    <header className="forge-page-header"><div><div className="forge-section-eyebrow">Módulo 04 · Trabajo</div><h1 className="forge-title">Elige un proyecto</h1><p className="forge-lead">FORGE mantiene el contexto de un proyecto a la vez para que las decisiones, Specs y entregas no se mezclen.</p></div></header>
    {error && <div className="message error">{error}</div>}
    <section className="workspace-local-demo" aria-label="Demo local de FORGE"><div><span>Demo local</span><h2>Recorre el golden path sin integraciones externas</h2><p>Crea un proyecto aislado y limpio para practicar Definir → Diseñar → Construir → Entregar. No crea repositorios, webhooks ni recursos cloud.</p></div><button type="button" className="forge-button primary" disabled={demoBusy} onClick={() => prepareLocalDemo()}>{demoBusy ? 'Preparando…' : 'Preparar demo local'}</button></section>
    <div className="workspace-project-grid">{repos.map((repo) => <Link key={repo.id} className="workspace-project-card" to={`/work?${contextQuery({ userId, username, repoId: repo.id })}`}><StatusPill color="blue">Proyecto</StatusPill><strong>{repo.full_name}</strong><span>{repo.description || 'Abrir espacio de trabajo'}</span><small>Abrir proyecto →</small></Link>)}{repos.length === 0 && <div className="forge-empty">Conecta o crea un repositorio desde Empezar.</div>}</div>
  </div>;

  const scopedBoard = <section className="workspace-scoped-board" aria-label="Specs del proyecto seleccionado"><div className="workspace-section-heading"><div><h2>Specs activas</h2><p>Solo aparecen Specs del proyecto seleccionado.</p></div><Link className="forge-button" to={`/specs?${contextQuery({ userId, username, repoId })}`}>Abrir lista de Specs</Link></div>{workItems.length ? <div className="workspace-spec-list">{workItems.map(({ session, stage, availability }) => <Link key={session.id} className="workspace-spec-card" to={`/specs/${session.id}?${contextQuery({ userId, username, repoId })}`}><StatusPill color={stage.color}>{stage.label}</StatusPill><strong>{session.title || session.spec_number || `Spec #${session.id}`}</strong><span>{stage.key === 'builder_ready' && availability.blocked ? `Bloqueada por ${availability.pendingDependencies.length} dependencia${availability.pendingDependencies.length === 1 ? '' : 's'}` : `${stage.role || 'FORGE'} · ${stage.action}`}</span></Link>)}</div> : <div className="forge-empty">Aún no hay Specs activas para este proyecto.</div>}</section>;

  return <div className="forge-page workspace-page">
    <header className="workspace-header"><div><div className="forge-section-eyebrow">Espacio de proyecto · Trabajar</div><h1 className="forge-title">{selectedRepo?.full_name || 'Proyecto'}</h1><p>{status.detail}</p>{spine?.local_demo && <p className="workspace-local-demo-note">Demo local aislada: evidencia simulada, sin GitHub ni AWS.</p>}</div><div className="workspace-header-controls"><label>Proyecto<select aria-label="Cambiar proyecto" value={repoId} onChange={changeProject}><option value="">Selecciona…</option>{repos.map((repo) => <option key={repo.id} value={repo.id}>{repo.full_name}</option>)}</select></label><StatusPill color={status.color}>{status.label}</StatusPill><span className="workspace-progress" aria-label="Progreso del proyecto">{progress.completed}/{progress.total} entregas</span>{spine?.local_demo && <button type="button" className="forge-button secondary" disabled={demoBusy} onClick={() => { if (window.confirm('Esto eliminará únicamente los datos del proyecto demo local y lo regresará a Definir. ¿Continuar?')) prepareLocalDemo(true); }}>{demoBusy ? 'Reiniciando…' : 'Reiniciar demo'}</button>}</div></header>
    {error && <div className="message error">{error}</div>}
    <nav className="workspace-sections" aria-label="Secciones del proyecto">{PROJECT_SECTIONS.map((item, index) => <Link key={item.key} to={`/work?${contextQuery({ userId, username, repoId, section: item.key })}`} className={item.key === activeSection ? 'active' : ''}><span>{index + 1}</span><div><b>{item.label}</b><small>{item.description}</small></div></Link>)}</nav>
    <section className="workspace-next-action" aria-label="Siguiente acción"><div><span>Siguiente acción</span><strong>{status.detail}</strong><small>Responsable: {status.owner}</small>{projectAction && <small>Acción: {projectAction.label}</small>}{projectActionBlock && <small>{projectActionBlock}</small>}</div><small>Las secciones siempre están visibles. Si una acción requiere otro rol, FORGE explica el bloqueo dentro de la sección.</small></section>
    {activeSection === 'build' && scopedBoard}
    <ProjectSpine section={activeSection} embedded onProjectChange={loadProject} />
  </div>;
}

export default Work;
